import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Box, Button, Typography, Grid2, Divider, Checkbox, Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { formatDateRange } from "../../../Helper/Analytics/AnalyticsHelper";
import BreakdownChart from "../../BmsFaults/Analytics/BreakdownChart";
import BMSFaultChart from "../../BmsFaults/Components/StyledComponents/BMSFaultChart";
import SecondsFormatter, { SecondsFormatterDetailed } from "../../../Helper/Formatters/SecondsFormatter";
import SearchInput from "../../Overview/components/SearchInput";
import {
  GroupByButton,
  DropdownMenu,
  GroupByMenuItem,
} from "../../BmsFaults/Components/StyledComponents";
import { getSmartAlertsList } from "../../../Api/SmartAlertsAnalyticsApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { SMART_ALERTS_GROUP_BY_OPTIONS } from "./breakdownOptions";
import { resolveBreakdownDimensions } from "../../../Helper/Analytics/breakdownDimensions";
import { useOrgLabels, usePagesContent } from "../../../Hooks/usePagesContent";


const ALL_ITEM_ID = "all";

const SIDEBAR_LIST_MAX_HEIGHT = "350px";

const buildChartConfig = (chartData, handleDataPointSelection) => {
  if (!chartData || chartData.length === 0) return { series: [], options: {} };

  const categories = chartData.map((item) => item.alertName);
  const rawData    = chartData.map((item) => item.value);
  const maxValue   = Math.max(...rawData);

  const NO_VALUE_MIN    = maxValue > 0 ? maxValue * 0.02 : 1;
  const MIN_WIDTH       = maxValue > 0 ? maxValue * 0.03 : 1;
  const seriesData      = rawData.map((v) => (v == null ? NO_VALUE_MIN : Math.max(v, MIN_WIDTH)));

  const formattedLabels = rawData.map((v) => SecondsFormatterDetailed(v, "short"));
  const longestLen      = Math.max(...formattedLabels.map((l) => l.length));
  const bufferPct       = Math.max(0.30, longestLen * 0.04);
  const xAxisMax        = Math.max(...seriesData) * (1 + bufferPct);

  const series = [{ name: "Avg. Resolution Duration", data: seriesData }];

  const options = {
    chart: {
      id: "resolution-duration-breakdown-chart",
      stacked: false,
      events: { dataPointSelection: handleDataPointSelection },
    },
    plotOptions: {
      bar: { dataLabels: { position: "top" } },
    },
    colors: ["#0B84FE"],
    dataLabels: {
      enabled: true,
      formatter: (value, opts) =>
        SecondsFormatterDetailed(rawData[opts?.dataPointIndex] ?? value, "short"),
      style: { fontSize: "12px", fontWeight: 400, colors: ["#494949"] },
      offsetX: 22,
      textAnchor: "start",
    },
    xaxis: {
      categories,
      max: xAxisMax,
      labels:     { show: false },
      axisTicks:  { show: false },
      axisBorder: { show: false },
    },
    yaxis: { show: false },
    legend: { show: false },
    tooltip: {
      shared: false,
      intersect: false,
      x: { formatter: (val, opts) => categories[opts?.dataPointIndex] || val },
      y: { formatter: (val, opts) => SecondsFormatterDetailed(rawData[opts?.dataPointIndex] ?? val, "short") },
    },
    grid: { show: false, padding: { left: 16, right: 0, top: -10, bottom: 24 } },
    states: { active: { filter: { type: "none" } } },
  };

  return { series, options };
};

const TOGGLE_BUTTON_SX = (active) => ({
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: active ? 500 : 400,
  textTransform: "none",
  borderRadius: "20px",
  padding: "6px 14px",
  backgroundColor: active ? "#F7F7FF" : "transparent",
  color: active ? "#6D72F6" : "#494949",
  border: active ? "1px solid #6D72F6" : "1px solid #E6E6E6",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: active ? "#F7F7FF" : "#F8F8F8",
    border: active ? "1px solid #6D72F6" : "1px solid #E6E6E6",
    boxShadow: "none",
  },
});

const mapChartRows = (rows) =>
  rows.map((row) => {
    const metrics = row.metricsArray || [];
    return {
      alertName:   row.xLabel,
      autoValue:   metrics.find((m) => m.type === "AutoResolvedAverage")?.value ?? 0,
      manualValue: metrics.find((m) => m.type === "ManualResolvedAverage")?.value ?? 0,
    };
  });

const ResolutionDurationBreakdownChartView = ({
  title,
  height = "600px",
  startDate,
  endDate,
  globalFilters = {},
  onDataPointClick,
}) => {
  const [resolutionType, setResolutionType]     = useState("auto");
  const pagesContent = usePagesContent();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();

  // The menu, from PlatformAlertsV2.breakdownDimensions where the org configures
  // it. The two customer dimensions take their names from the same org meta row
  // that names them in the filter and the columns.
  const groupByOptions = useMemo(
    () =>
      resolveBreakdownDimensions(
        pagesContent?.metaInfo?.breakdownDimensions,
        SMART_ALERTS_GROUP_BY_OPTIONS,
        { fleetName: `By ${fleetLabel}`, fleetCategory: `By ${fleetCategoryLabel}` }
      ),
    [pagesContent, fleetLabel, fleetCategoryLabel]
  );

  const [groupBy, setGroupBy]                   = useState("originCity");
  const [selectedOption, setSelectedOption]     = useState(SMART_ALERTS_GROUP_BY_OPTIONS[0]);
  const [selectedItems, setSelectedItems]       = useState([]);
  const [searchQuery, setSearchQuery]           = useState("");
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [allChartData, setAllChartData]         = useState([]);
  const [sidebarItems, setSidebarItems]         = useState([]);

  const selectedItemsRef   = useRef([]);
  const sidebarItemsRef    = useRef([]);
  const resolutionTypeRef  = useRef(resolutionType);
  const onClickRef         = useRef(onDataPointClick);
  const handleItemTimerRef = useRef(null);

  useEffect(() => { resolutionTypeRef.current = resolutionType; }, [resolutionType]);
  useEffect(() => { onClickRef.current = onDataPointClick; }, [onDataPointClick]);

  const chartData = useMemo(() => {
    const key = resolutionType === "auto" ? "autoValue" : "manualValue";
    return allChartData
      .map((row) => ({ alertName: row.alertName, value: row[key] }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [allChartData, resolutionType]);

  const chartDataRef = useRef(chartData);
  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);

  const fetchChartData = useCallback((selectedItemsArr, currentGroupBy, currentStartDate, currentEndDate) => {
    const isAllSelected = selectedItemsArr.some((i) => i.id === ALL_ITEM_ID);
    let filters;
    if (isAllSelected || selectedItemsArr.length === 0) {
      const allIds = sidebarItemsRef.current
        .filter((i) => i.id !== ALL_ITEM_ID)
        .map((i) => i.id);
      filters = allIds.length > 0 ? [{ name: currentGroupBy, values: allIds }] : [];
    } else {
      filters = [{ name: currentGroupBy, values: selectedItemsArr.map((i) => i.id) }];
    }

    return getSmartAlertsList(globalFilters, {
      startDate: currentStartDate,
      endDate:   currentEndDate,
      select:    ["avgResolutionDuration"],
      groupBy:   ["alertDescription", "ResolutionType"],
      ...(filters.length ? { filters } : {}),
    });
  }, [globalFilters]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);

    getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:  ["ResolvedAlertCount"],
      groupBy: [groupBy],
    }).then((sidebarRes) => {
      if (cancelled) return;

      const rows = sidebarRes?.responseStatus?.code === CODE.SUCCESS
        ? sidebarRes.response?.data || []
        : [];

      const items = rows.map((row) => ({
        id:    row.xLabel,
        label: row.xLabel,
        count: row.metricsArray?.find((m) => m.type === "ResolvedAlertCount")?.value ?? 0,
      }));

      const totalCount = items.reduce((sum, i) => sum + i.count, 0);
      const allItems = [{ id: ALL_ITEM_ID, label: "All", count: totalCount }, ...items];

      setSidebarItems(allItems);
      sidebarItemsRef.current = allItems;
      const defaultItem = allItems[0];
      setSelectedItems([defaultItem]);
      selectedItemsRef.current = [defaultItem];
      setSearchQuery("");

      return fetchChartData([defaultItem], groupBy, startDate, endDate);
    }).then((chartRes) => {
      if (cancelled || !chartRes) return;

      const rows = chartRes?.responseStatus?.code === CODE.SUCCESS
        ? chartRes.response?.data || []
        : [];

      setAllChartData(mapChartRows(rows));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [groupBy, startDate, endDate, globalFilters, fetchChartData]);

  const handleItemToggle = useCallback((item) => {
    if (loading) return;

    const prev = selectedItemsRef.current;
    const isAllSelected = prev.some((i) => i.id === ALL_ITEM_ID);
    let next;
    if (item.id === ALL_ITEM_ID) {
      next = isAllSelected ? [] : [item];
    } else {
      if (isAllSelected) {
        next = sidebarItemsRef.current.filter((i) => i.id !== ALL_ITEM_ID && i.id !== item.id);
      } else {
        next = prev.some((i) => i.id === item.id)
          ? prev.filter((i) => i.id !== item.id)
          : [...prev, item];
      }
    }
    const allIndividual = sidebarItemsRef.current.filter((i) => i.id !== ALL_ITEM_ID);
    if (next.length === allIndividual.length && !next.some((i) => i.id === ALL_ITEM_ID)) {
      const allItem = sidebarItemsRef.current.find((i) => i.id === ALL_ITEM_ID);
      if (allItem) next = [allItem];
    }

    selectedItemsRef.current = next;
    setSelectedItems(next);

    if (next.length === 0) {
      setAllChartData([]);
      return;
    }

    setLoading(true);
    clearTimeout(handleItemTimerRef.current);
    handleItemTimerRef.current = setTimeout(() => {
      fetchChartData(next, groupBy, startDate, endDate).then((chartRes) => {
        const rows = chartRes?.responseStatus?.code === CODE.SUCCESS
          ? chartRes.response?.data || []
          : [];
        setAllChartData(mapChartRows(rows));
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 150);
  }, [loading, groupBy, startDate, endDate, fetchChartData]);

  const handleDataPointSelection = useCallback((event, chartContext, config) => {
    const curData     = chartDataRef.current;
    const cats        = curData.map((d) => d.alertName);
    const di          = config.dataPointIndex;
    const resType     = resolutionTypeRef.current;
    const seriesLabel = resType === "auto" ? "Auto Resolved" : "Manually Resolved";

    const clickedData = {
      faultName:      cats[di] || "",
      value:          curData[di]?.value || 0,
      dataPointIndex: di,
      seriesIndex:    0,
      selectedItems:  selectedItemsRef.current,
      groupBy:        selectedOption,
      resolutionType: seriesLabel,
    };

    if (onClickRef.current) onClickRef.current(clickedData, selectedOption.value);
  }, [selectedOption]);

  const filteredItems = sidebarItems.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!groupByOptions.length) return;
    if (groupByOptions.some((option) => option.value === groupBy)) return;
    setGroupBy(groupByOptions[0].value);
    setSelectedOption(groupByOptions[0]);
  }, [groupByOptions, groupBy]);

  const chartConfig = useMemo(
    () => buildChartConfig(chartData, handleDataPointSelection),
    [chartData, handleDataPointSelection]
  );

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
        height: height,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 22px",
        }}
      >
        <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
          {title}
        </Typography>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", height: `calc(${height} - 72px)`, overflow: "hidden" }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: "320px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mb: 2,
            padding: "0px 0px 30px 0px",
          }}
        >
          <Box sx={{ padding: "0 16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Tooltip
                title={selectedOption?.shortLabel ? selectedOption.label : ""}
                placement="top"
                arrow
              >
                <GroupByButton
                  onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                  startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                >
                  {selectedOption.label}
                </GroupByButton>
              </Tooltip>
              <Typography variant="fw400s12c686868">ISSUES</Typography>
            </Box>

            <Divider sx={{ mb: 1, borderColor: "#E4E4E4" }} />

            <Box sx={{ mb: 1 }}>
              <SearchInput
                placeholder={`Search ${selectedOption.label.replace("By ", "") || "City"}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            <DropdownMenu
              anchorEl={dropdownAnchorEl}
              open={Boolean(dropdownAnchorEl)}
              onClose={() => setDropdownAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {groupByOptions.map((option) => (
                <GroupByMenuItem
                  key={option.value}
                  selected={groupBy === option.value}
                  onClick={() => {
                    setGroupBy(option.value);
                    setSelectedOption(option);
                    setSearchQuery("");
                    setDropdownAnchorEl(null);
                  }}
                >
                  {option.label}
                </GroupByMenuItem>
              ))}
            </DropdownMenu>
          </Box>

          <Box sx={{ maxHeight: SIDEBAR_LIST_MAX_HEIGHT, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
            {loading ? (
              <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                <CartLoader />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ padding: "16px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">No items found</Typography>
              </Box>
            ) : (
              <Grid2 container gap={0.5} flexDirection="column">
                {filteredItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleItemToggle(item)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      cursor: "pointer",
                      "&:hover": { opacity: 0.8 },
                      transition: "opacity 0.2s",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        checked={
                          selectedItems.some((i) => i.id === ALL_ITEM_ID) ||
                          selectedItems.some((i) => i.id === item.id)
                        }
                        indeterminate={
                          item.id === ALL_ITEM_ID &&
                          !selectedItems.some((i) => i.id === ALL_ITEM_ID) &&
                          selectedItems.length > 0
                        }
                        sx={{
                          padding: 0,
                          color: "#6D72F6",
                          "&.Mui-checked":               { color: "#6D72F6" },
                          "&.MuiCheckbox-indeterminate": { color: "#6D72F6" },
                          "& .MuiSvgIcon-root":          { fontSize: "20px" },
                        }}
                      />
                      <Typography
                        variant="fw400s14c000000"
                        sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.label || "Not Set"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="fw600s14c000000"
                      sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            )}
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mb: 6 }} />

        {/* Right — Chart */}
        <Box
          sx={{
            flex: 1,
            padding: "12px 28px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          {/* Toggle + label */}
          <Box>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-start", mb: 2 }}>
              <Button
                variant={resolutionType === "auto" ? "contained" : "outlined"}
                onClick={() => setResolutionType("auto")}
                sx={TOGGLE_BUTTON_SX(resolutionType === "auto")}
              >
                Auto Resolved
              </Button>
              <Button
                variant={resolutionType === "manual" ? "contained" : "outlined"}
                onClick={() => setResolutionType("manual")}
                sx={TOGGLE_BUTTON_SX(resolutionType === "manual")}
              >
                Manually Resolved
              </Button>
            </Box>
            <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: "uppercase", color: "#8F8F8F" }}>
              Alert Name
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            {loading ? (
              <Box
                sx={{
                  padding: "32px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <CartLoader />
              </Box>
            ) : selectedItems.length === 0 ? (
              <Box sx={{ padding: "32px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">Select at least one item</Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ padding: "32px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">
                  {`No data available for ${resolutionType === "auto" ? "Auto Resolved" : "Manually Resolved"} alerts`}
                </Typography>
              </Box>
            ) : (
              <BMSFaultChart drillDownEnabled={true}>
                <BreakdownChart
                  series={chartConfig.series}
                  categories={chartConfig.options?.xaxis?.categories || []}
                  options={chartConfig.options}
                  minVisibilityHeight={360}
                  xAxisTickFormatter={(v) => SecondsFormatter(v, "short")}
                />
              </BMSFaultChart>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResolutionDurationBreakdownChartView;
